import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Helmet} from "react-helmet";
import TmdbScore from "../../tmdbScore/tmdbScore";
import {Spinner} from "../../../shared";
import {useDispatch, useSelector} from "react-redux";
import cap17drive from "../../index";
import {cn, getSerieDates} from "../../../../utils/utils";
import TmdbNotepad from "../tmdbNotepad/tmdbNotepad";
import TmdbViewLater from "../tmdbViewLater/TmdbViewLater";
import users from "../../../users";
import TmdbCollectionSelector from "../tmdbCollectionSelector/tmdbCollectionSelector";
import ButtonV2 from "../../../shared/button-v2/button-v2";

import './serieDetails.scss';
import useScrollContainerRef from "../utils/useScrollContainerRef";
import SeasonSelector from "./seasonSelector/seasonSelector";
import SeasonDetails from "./seasonDetails/seasonDetails";
import TmdbItemSuggestions from "../tmdb-item-suggestions/tmdb-item-suggestions";
import TmdbImage from "../../../shared/tmdb-image/tmdb-image";
import TmdbLikeItem from "../tmdbLikeItem/tmdb-like-item";
import TmdbItemReviews from "../tmdb-item-reviews/tmdb-item-reviews";
import useSerieDetailsData, {isFirstSerieEpisode as isFirstEpisode} from "../../hooks/useSerieDetailsData";

function SerieDetails({squadflixTvHook}) {
    const {id} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const darkTheme = useSelector(users.selectors.isDarkMode);
    const containerRef = useRef(null);
    const [backdropOpacity, setBackdropOpacity] = useState(0);
    const parentRef = useScrollContainerRef();
    const [selectedRoute, setSelectedRoute] = useState("episodes");
    const {
        serie,
        season,
        setSeason,
        seasonLoaded,
        lastEpisode,
        logoImage,
        backgroundImage,
        backgroundAspectRatio,
        from,
        handleClickPlay
    } = useSerieDetailsData(id);

    const routes = [{
        route: "episodes",
        name: "Episodios",
    }, {
        route: "reviews",
        name: "Reseñas",
    }, {
        route: "suggestions",
        name: "Sugerencias",
    }, {
        route: "details",
        name: "Detalles",
    }, {
        onClick: () => {
            navigate("#notes")
        },
        name: "Mis Notas",
    }]

    useEffect(() => {
        setSelectedRoute("episodes");
    }, [id]);

    useLayoutEffect(() => {
        if (!parentRef.ref) {
            return;
        }

        parentRef.ref.current.addEventListener("scroll", () => {
            const scrollTop = parentRef.ref.current.scrollTop;
            const newOpacity = Math.min(scrollTop / 300, 0.6);
            setBackdropOpacity(newOpacity);
        });
    }, [parentRef.ref]);

    if (!serie) {
        return (
            <center className="mt-4">
                <Spinner></Spinner>
            </center>
        )
    }

    return (
        <div className={cn("serie-details", {"serie-details-dark": darkTheme})} ref={containerRef}>
            <Helmet>
                <meta charSet="utf-8"/>
                <title>{serie.name}</title>
                <link rel="icon"
                      href="https://torresowo.myftp.org/web/cap17an/cap17drive/imagenes/Icono-Cap17Drive.png"/>
            </Helmet>
            <div className="serie-details__top-header" style={{
                backgroundColor: backdropOpacity > 0.55 ? (darkTheme ? "rgb(33,37,41)" : "white") : "transparent",
                width: parentRef.width,
            }}>
                <ButtonV2
                    className="serie-details__back-button"
                    width={(from.customKey && backdropOpacity <= 0.55) ? "fit-content" : "50px"}
                    onClick={() => {
                        dispatch(cap17drive.actions.clearSelectedEpisode());
                        dispatch(cap17drive.actions.clearSelectedSerie());
                        navigate(from.pathName)
                    }}>
                    <i className={cn("fa-solid fa-chevron-left fa-lg", {"pe-3": backdropOpacity <= 0.55 && from.customKey})}></i>
                    {backdropOpacity <= 0.55 && from.customKey}
                </ButtonV2>
                {backdropOpacity > 0.55 && <div className="serie-details__top-header-title">{serie.name}</div>}
            </div>
            <div style={{
                position: "relative",
                zIndex: 2,
                width: "100%",
            }}>
                <div className="serie-details__header" style={{
                    height: "fit-content"
                }}>
                    {logoImage &&
                        <TmdbImage className="serie-details__header--logo" src={logoImage.file_path}/>}
                    {!logoImage && <div className="serie-details__header--title-placeholder">
                        {serie.name}</div>}

                    <div className="serie-details__header--categories">
                        {serie.genres.map((genre, index) => (
                            <span className="serie-details__header--categories-item" key={genre.id}>
                                {genre.name}
                            </span>
                        ))}
                        <span className="serie-details__header--categories-item">
                            {serie.vote_average.toFixed(1)} <i className="fa-solid fa-star fa-xs"></i>
                        </span>
                    </div>
                    <div className="serie-details__header--info text-muted">
                        <span>{getSerieDates(serie)}</span>
                        &nbsp;· {serie.seasons?.length ?? 0} {serie.number_of_seasons === 1 ? "Temporada" : "Temporadas"}
                    </div>
                    <div className="serie-details__header--overview">
                        {serie.overview}
                    </div>
                    <div className="serie-details__header--actions">
                        <ButtonV2 className="serie-details__header--actions-play"
                                  onClick={handleClickPlay}>
                            <i className="fa-solid fa-play pe-3 fa-lg"></i>
                            {(isFirstEpisode(lastEpisode) || lastEpisode == null) ? "Ver" :
                                <>Ver S{lastEpisode.season_number}:E{lastEpisode.episode_number}</>}
                        </ButtonV2>
                        <TmdbCollectionSelector item={serie} isMovie={false}/>
                        <TmdbViewLater tmdbItem={serie}></TmdbViewLater>
                        <TmdbLikeItem tmdbItem={serie}/>
                    </div>
                </div>
                <div className="serie-details__content">
                    <div className="serie-details__content--routes">
                        {
                            routes.map((route) => (
                                <button
                                    onClick={() => route.route ? setSelectedRoute(route.route) : route.onClick?.()}
                                    key={route.route ?? route.name}
                                    className={cn("serie-details__content--routes-item", {
                                        "serie-details__content--routes-item--active": route.route === selectedRoute
                                    })}>
                                    {route.name}
                                </button>
                            ))

                        }
                    </div>
                    {selectedRoute === "episodes" && <div>
                        <SeasonSelector seasons={serie.seasons} selectedSeason={season ?? seasonLoaded}
                                        setSelectedSeason={setSeason}></SeasonSelector>
                        <SeasonDetails seasonNumber={season} serie={serie}></SeasonDetails>
                    </div>}
                    {selectedRoute === "suggestions" && <div>
                        <TmdbItemSuggestions item={serie} isMovie={false}/>
                    </div>}
                    {selectedRoute === "details" &&
                        <div>
                            <div className="serie-details__header--title-placeholder w-100 mt-4">
                                {serie.name}
                            </div>
                            <div className="row mt-3 mb-3">
                                <div className="col-md-6 pt-4">
                                    {serie.overview}
                                </div>
                                <div className="col-md-6">
                                    <TmdbScore score={serie.vote_average}></TmdbScore>
                                </div>
                            </div>
                        </div>
                    }
                    {selectedRoute === "reviews" &&
                        <div className="mt-4 mb-3">
                            <TmdbItemReviews item={serie} isMovie={false}/>
                        </div>
                    }
                </div>
            </div>
            <div
                className="serie-details__background-image"
                style={{
                    width: parentRef.width - 2,
                    height: parentRef.width * backgroundAspectRatio,
                }}>
                <div className="serie-details__background-image--backdrop" style={{
                    opacity: backdropOpacity,
                }}></div>
                <div className="serie-details__background-image--overlay"></div>
                {backgroundImage && <TmdbImage
                    className="serie-details__background-image--still"
                    src={backgroundImage.file_path} size={"original"}/>}
            </div>

            <div style={{display: "none"}}>
                <TmdbNotepad tmdbItem={serie}/> {/* Notas  NO BORRAR*/}
            </div>
        </div>
    );

}

export default SerieDetails;
